import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle, XCircle, Trash2, PenTool, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useConferma } from "../components/ConfirmProvider";

export default function SignaturePage() {
  const { token } = useParams();
  const canvasRef = useRef(null);
  const [document, setDocument] = useState(null);
  const conferma = useConferma();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchDocument();
  }, [token]);

  useEffect(() => {
    if (document && canvasRef.current) {
      initCanvas();
    }
  }, [document]);

  const fetchDocument = async () => {
    try {
      const response = await axios.get(`/api/signature/public/${token}`);
      setDocument(response.data);
      setSignerName(response.data.client_name || "");
    } catch (err) {
      if (err.response?.status === 410) {
        setError("expired");
      } else if (err.response?.status === 400) {
        setError("already_signed");
      } else {
        setError("not_found");
      }
    } finally {
      setLoading(false);
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    
    // Set canvas size
    const container = canvas.parentElement;
    canvas.width = container.offsetWidth - 2;
    canvas.height = 200;
    
    // Set drawing style
    ctx.strokeStyle = "#1e40af";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    
    // White background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const getPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPosition(e);
    
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPosition(e);
    
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const submitSignature = async () => {
    if (!hasSignature) {
      toast.error("Per favore, firma il documento");
      return;
    }
    
    if (!signerName.trim()) {
      toast.error("Per favore, inserisci il tuo nome");
      return;
    }

    // v3.9.12 — la firma è un atto definitivo: si conferma esplicitamente
    if (!(await conferma({
      titolo: "Confermi la firma del documento?",
      descrizione:
        "Firmatario: " + signerName.trim() + "\n\n" +
      "Dopo la conferma la firma viene registrata e non è più modificabile.",
    }))) return;

    setSubmitting(true);
    
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL("image/png");
      
      await axios.post(`/api/signature/public/${token}/sign`, {
        signature_data: signatureData,
        signer_name: signerName.trim()
      });
      
      setSubmitted(true);
      toast.success("Documento firmato con successo!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante la firma");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-gray-600">Caricamento documento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            {error === "expired" && (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Link Scaduto</h2>
                <p className="text-gray-600">
                  Questo link per la firma è scaduto. Contatta il tuo broker per ricevere un nuovo link.
                </p>
              </>
            )}
            {error === "already_signed" && (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Già Firmato</h2>
                <p className="text-gray-600">
                  Questo documento è già stato firmato.
                </p>
              </>
            )}
            {error === "not_found" && (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Documento Non Trovato</h2>
                <p className="text-gray-600">
                  Il documento richiesto non esiste o è stato rimosso.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Firma Completata!</h2>
            <p className="text-gray-600 mb-4">
              Grazie! Il documento è stato firmato con successo.
            </p>
            <p className="text-sm text-gray-500">
              Puoi chiudere questa pagina.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">{document.document_title}</h1>
          <p className="text-gray-600 mt-1">Documento da firmare per {document.client_name}</p>
        </div>

        {/* Document Content */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Contenuto del Documento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              {document.document_content.split("\n").map((line, i) => (
                <p key={i} className={`${line.trim() ? "mb-2" : "mb-4"} text-gray-700 text-sm leading-relaxed`}>
                  {line || "\u00A0"}
                </p>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Signature Area */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PenTool className="h-5 w-5" />
              La Tua Firma
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="signer_name">Nome e Cognome</Label>
              <Input
                id="signer_name"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value.toUpperCase())}
                placeholder="MARIO ROSSI"
                className="uppercase"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Firma qui sotto con il dito o il mouse</Label>
                <Button variant="ghost" size="sm" onClick={clearCanvas}>
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Cancella
                </Button>
              </div>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-1 bg-white">
                <canvas
                  ref={canvasRef}
                  className="w-full cursor-crosshair touch-none rounded"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                Usa il dito sul touchscreen o il mouse per disegnare la tua firma
              </p>
            </div>

            <Button 
              className="w-full" 
              size="lg"
              onClick={submitSignature}
              disabled={submitting || !hasSignature || !signerName.trim()}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Invio in corso...
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Firma e Conferma
                </>
              )}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              Cliccando "Firma e Conferma" dichiari di aver letto e accettato il contenuto del documento.
              La firma ha valore di firma elettronica semplice ai sensi del Regolamento eIDAS.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
